import React from "react";
import { useSearchParams } from "react-router-dom";
import { Paintbrush, ListFilter, Settings2 } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PlatformBranding from "@/pages/admin/PlatformBranding";
import PlatformConfiguration from "@/pages/admin/PlatformConfiguration";
import PlatformLookups from "@/pages/admin/PlatformLookups";

const VALID_TABS = new Set(["branding", "lookups", "advanced"]);

const normalizeTab = (value) => {
  const next = String(value || "").toLowerCase();
  return VALID_TABS.has(next) ? next : "branding";
};

export default function PlatformSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = normalizeTab(searchParams.get("tab"));

  const onTabChange = (tab) => {
    const next = normalizeTab(tab);
    setSearchParams({ tab: next }, { replace: true });
  };

  return (
    <div className="space-y-4" data-testid="platform-settings-page">
      <div className="px-6 pt-6 lg:px-8">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)] flex items-center gap-2">
          <Settings2 className="h-6 w-6 text-[var(--accent)]" /> Platform Settings
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          One place to manage branding, lookup values, and advanced JSON configuration.
        </p>
      </div>

      <div className="px-6 lg:px-8">
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList className="w-full max-w-2xl h-auto p-1.5 flex flex-wrap justify-start gap-1">
            <TabsTrigger value="branding" className="gap-1.5">
              <Paintbrush className="h-4 w-4" /> Branding
            </TabsTrigger>
            <TabsTrigger value="lookups" className="gap-1.5">
              <ListFilter className="h-4 w-4" /> Lookups
            </TabsTrigger>
            <TabsTrigger value="advanced" className="gap-1.5">
              <Settings2 className="h-4 w-4" /> Advanced Config
            </TabsTrigger>
          </TabsList>

          <TabsContent value="branding" className="mt-4">
            <PlatformBranding />
          </TabsContent>

          <TabsContent value="lookups" className="mt-4">
            <PlatformLookups />
          </TabsContent>

          <TabsContent value="advanced" className="mt-4">
            <PlatformConfiguration />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
